import { useEffect, useRef, useState, type CSSProperties, type FormEvent } from 'react';
import { getCabinetId, type EasterEggId } from '../lib/easterEggProgress';
import type { CharacterProfile } from '../types';

interface EasterEggCameoProps {
  cameo: CharacterProfile;
  elapsedMs: number;
  leftName: string;
  matchupKey: string;
  onPause: () => void;
  onResume: () => void;
  onDiscover: (id: EasterEggId) => void;
  rightName: string;
}

const UNLOCKS_KEY = 'smackers-easter-egg-unlocks-v1';
const VANDAL_CAMEO = '/assets/characters/vandal/idle/previews/vandal_idle_preview.gif?v=20260717-5';

function rememberUnlock(matchupKey: string, proposedName: string) {
  try {
    const saved = JSON.parse(localStorage.getItem(UNLOCKS_KEY) ?? '{}') as Record<string, string>;
    localStorage.setItem(UNLOCKS_KEY, JSON.stringify({ ...saved, [matchupKey]: proposedName }));
  } catch {
    // A successful Discord submission does not depend on local storage access.
  }
}

export function EasterEggCameo({ cameo, elapsedMs, leftName, matchupKey, onPause, onResume, onDiscover, rightName }: EasterEggCameoProps) {
  const [opened, setOpened] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [submittedBy, setSubmittedBy] = useState('');
  const [proposedName, setProposedName] = useState('');
  const [phase, setPhase] = useState<'form' | 'sending' | 'thanks' | 'error'>('form');
  const [error, setError] = useState('');
  const resumeTimerRef = useRef<number | null>(null);
  const cameoVisible = elapsedMs >= 4500 && elapsedMs < 7000 && !opened && !dismissed;

  useEffect(() => () => {
    if (resumeTimerRef.current !== null) window.clearTimeout(resumeTimerRef.current);
  }, []);

  const unlock = () => {
    onDiscover('boss-signal');
    setDismissed(true);
    setOpened(true);
    onPause();
  };

  const resume = () => {
    setOpened(false);
    onResume();
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const playerName = submittedBy.trim();
    const name = proposedName.trim();
    if (playerName.length < 2 || playerName.length > 32) {
      setError('Enter your name using 2–32 characters.');
      setPhase('error');
      return;
    }
    if (name.length < 2 || name.length > 32) {
      setError('Use 2–32 characters for the fighter name.');
      setPhase('error');
      return;
    }
    setError('');
    setPhase('sending');
    try {
      const response = await fetch('/api/easter-egg-name', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cabinetId: getCabinetId(),
          cameoCharacterId: cameo.id,
          cameoCharacterName: cameo.name,
          leftName,
          matchupKey,
          proposedName: name,
          rightName,
          submittedBy: playerName,
          website: '',
        }),
      });
      if (!response.ok) throw new Error('The cabinet link did not answer.');
      rememberUnlock(matchupKey, name);
      setPhase('thanks');
      resumeTimerRef.current = window.setTimeout(resume, 1900);
    } catch {
      setError('Could not send that name yet. Try again or return to the attract mode.');
      setPhase('error');
    }
  };

  return (
    <>
      {cameoVisible && (
        <button className="easter-cameo easter-cameo--vandal" type="button" onClick={unlock} aria-label="Secret fighter signal from Vandal. Activate it." data-testid="easter-cameo">
          <span className="easter-cameo__copy"><strong>SMACK CONFIRMED!</strong><b>VANDAL SAW THAT.</b></span>
          <img src={VANDAL_CAMEO} alt="Vandal slides into the match" width="384" height="384" />
        </button>
      )}
      {opened && (
        <div className="easter-unlock" role="dialog" aria-modal="true" aria-labelledby="easter-unlock-title" data-testid="easter-unlock">
          <div className="easter-unlock__fireworks" aria-hidden="true">
            {Array.from({ length: 20 }, (_, index) => <i key={index} style={{ '--spark': index } as CSSProperties} />)}
          </div>
          <div className="easter-unlock__panel">
            <img src={VANDAL_CAMEO} alt="" width="256" height="256" />
            <p>SECRET SIGNAL // BOSS ENCOUNTER</p>
            <h1 id="easter-unlock-title">SPECIAL CHARACTER UNLOCKED</h1>
            {phase === 'thanks' ? (
              <div className="easter-unlock__thanks" aria-live="polite"><strong>THANKS!</strong><span>NAME TRANSMITTED TO L34D SOFTWARE</span></div>
            ) : (
              <form onSubmit={submit}>
                <div className="easter-unlock__fields">
                  <div className="easter-unlock__field">
                    <label htmlFor="special-character-submitter">Your name</label>
                    <input id="special-character-submitter" name="submitted-by" value={submittedBy} onChange={(event) => setSubmittedBy(event.target.value)} maxLength={32} autoComplete="name" autoFocus disabled={phase === 'sending'} />
                  </div>
                  <div className="easter-unlock__field">
                    <label htmlFor="special-character-name">Special character name</label>
                    <input id="special-character-name" name="special-character-name" value={proposedName} onChange={(event) => setProposedName(event.target.value)} maxLength={32} autoComplete="off" disabled={phase === 'sending'} />
                  </div>
                </div>
                <button type="submit" disabled={phase === 'sending'}>{phase === 'sending' ? 'SENDING…' : 'SEND NAME'}</button>
                {error && <p className="easter-unlock__error" role="alert">{error}</p>}
                <button className="easter-unlock__return" type="button" onClick={resume}>RETURN TO ATTRACT MODE</button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
